import * as React from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    CardContent,
    Drawer,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    debounce,
    Button
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import CloseIcon from '@mui/icons-material/Close';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { EnhancedTableHeadProps, HeadCell, ArrangementOrder } from 'types';
import useAuth from 'hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import SkeletonBody from 'ui-component/skeleton/SkeletonBody';
import Utilities from 'utils/utilities';
import { useDispatch } from 'store';
import { startSnackbar } from 'store/slices/snackbar';
import { Firmware } from 'types/firmware';
import FirmwareService from 'services/FirmwareService';
import FirmwareFilter from 'types/filters/firmwareFilter';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import FirmwareListMobile from './firmwareListMobile';
import FirmwareAdd from './firmwareAdd';
import FirmwareEdit from './firmwareEdit';
import DownloadIcon from '@mui/icons-material/Download';
import NoteIcon from '@mui/icons-material/Note';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import useConfig from 'hooks/useConfig';

// table header options
const headCells: HeadCell[] = [
    {
        id: 'version',
        numeric: false,
        label: 'VERSION',
        align: 'left'
    },
    {
        id: 'releaseDate',
        numeric: false,
        label: 'RELEASE_DATE',
        align: 'left'
    },
    {
        id: 'internalVersion',
        numeric: false,
        label: 'INTERNAL_VERSION',
        align: 'center'
    }
];

// ==============================|| TABLE HEADER ||============================== //

interface GatewayListEnhancedTableHeadProps extends EnhancedTableHeadProps {
    selected: string[];
}

function EnhancedTableHead({
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    selected
}: GatewayListEnhancedTableHeadProps) {
    // const createSortHandler = (property: string) => (event: React.SyntheticEvent<Element, Event>) => {
    //     onRequestSort(event, property);
    // };
    const { t } = useTranslation();
    const currentTheme = useTheme();
    const mobileDevice = useMediaQuery(currentTheme.breakpoints.down('md'));
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
    }, []);

    return (
        <TableHead>
            <TableRow>
                {!mobileDevice &&
                    headCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.align}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                        >
                            {t(headCell.label)}
                        </TableCell>
                    ))}
                {numSelected <= 0 && (
                    <TableCell
                        sortDirection={false}
                        align="right"
                        sx={{ paddingRight: windowWidth < 1400 ? '1.5%!important' : '2%!important' }}
                    >
                        {t('ACTIONS')}
                    </TableCell>
                )}
            </TableRow>
        </TableHead>
    );
}

const FirmwareList = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { t } = useTranslation();
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const utilities = new Utilities();
    const { locale } = useConfig();

    const [order, setOrder] = useState<ArrangementOrder>('desc');
    const [orderBy, setOrderBy] = useState<string>('version');
    const [selected, setSelected] = useState<string[]>([]);
    const [page, setPage] = useState<number>(0);
    const [rowsPerPage, setRowsPerPage] = useState<number>(50);
    const [searchText, setSearchText] = useState<string>('');
    const [rows, setRows] = useState<Firmware[]>([]);
    const [totalElements, setTotalElements] = useState<number>(0);
    const [firmwareToEdit, setFirmwareToEdit] = useState<Firmware | undefined>(undefined);
    const [firmwareToAdd, setFirmwareToAdd] = useState<Firmware | undefined>(undefined);
    const [firmwareToPromote, setFirmwareToPromote] = useState<Firmware | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [fliterDialogModalOpen, setFliterDialogModalOpen] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const firmwareService = new FirmwareService();

    useEffect(() => {
        (async () => {
            if (user) {
                const firmwareFilter = getFirmwareFilter();
                setIsLoading(true);
                await fetchRow(firmwareFilter);
            }
        })();
    }, [user, page, rowsPerPage, order, orderBy, searchText]);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
    }, []);

    const fetchRow = useCallback(
        debounce(async (firmwareFilter: FirmwareFilter) => {
            const paginatedFirmwares = await firmwareService.getFirmwares(firmwareFilter);
            setIsLoading(false);
            setRows(paginatedFirmwares.items);
            setTotalElements(paginatedFirmwares.totalCount);
        }, 700),
        []
    );

    const getFirmwareFilter = () => {
        const firmwareFilter: FirmwareFilter = {
            orderAscending: order?.toString() === 'asc',
            orderBy: orderBy,
            page: page + 1,
            recordsPerPage: rowsPerPage,
            searchString: searchText
        };
        return firmwareFilter;
    };

    const handleSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        const newString = event?.target.value;
        setSearchText(newString || '');
    };

    const handleRequestSort = (event: React.SyntheticEvent<Element, Event>, property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelectedId = rows.map((n) => n.id);
            setSelected(newSelectedId);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | undefined) => {
        event?.target.value && setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const onAdd = async (firmware: Firmware) => {
        if (firmware && utilities.isNotNullOrEmpty(firmware.major!.toString())) {
            if (user) {
                setIsLoading(true);
                const response = await firmwareService.addFirmware(firmware);
                if (response) {
                    setFirmwareToAdd(undefined);
                    dispatch(startSnackbar(t('FIRMWARE_ADDED_SUCCESSFULLY'), 'success'));
                } else {
                    setIsLoading(false);
                }
                const firmwareFilter = getFirmwareFilter();
                await fetchRow(firmwareFilter);
            }
        } else {
            dispatch(startSnackbar(t('MAJOR_IS_MANDATORY'), 'error'));
        }
    };

    const onSave = async (firmware: Firmware) => {
        if (firmware && utilities.isNotNullOrEmpty(firmware.major!.toString())) {
            setIsLoading(true);
            const response = await firmwareService.editFirmware(firmware);
            if (response) {
                setFirmwareToEdit(undefined);
                dispatch(startSnackbar(t('FIRMWARE_EDITED_SUCCESSFULLY'), 'success'));
                const firmwareFilter = getFirmwareFilter();
                await fetchRow(firmwareFilter);
            } else {
                setIsLoading(false);
            }
        } else {
            dispatch(startSnackbar(t('MAJOR_IS_MANDATORY'), 'error'));
        }
    };

    const isSelected = (name: string) => selected.indexOf(name) !== -1;

    const handleChangeOrder = (e: any) => {
        const { value } = utilities.getFormValue(e);
        setOrderBy(value);
    };

    const handleChangeOrderMethod = (e: any) => {
        const { value } = utilities.getFormValue(e);
        setOrder(value === 'asc' ? 'asc' : 'desc');
    };

    const setDefaultFilters = () => {
        setOrder('asc');
        setOrderBy('version');
    };

    const downloadFirmware = async (firmware: Firmware) => {
        try {
            let fileName = `${firmware.major}.${firmware.minor}`;
            if (firmware.internal) {
                fileName = `${fileName}-internal`;
            }
            const firmwareZip = await firmwareService.downloadFirmware(fileName);
            const byteArray = new Uint8Array(firmwareZip?.bytes || []);

            const blob = new Blob([byteArray], { type: 'application/zip' });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            link.download = `${fileName}.zip`;

            document.body.appendChild(link);
            link.click();
        } catch (error) {
            console.error('Error downloading firmware:', error);
        }
    };

    const promoteFirmware = async (firmwareId: string) => {
        setIsLoading(true);
        const result = await firmwareService.promoteFirmware(firmwareId);
        if (result) {
            dispatch(startSnackbar(t('FIRMWARE_PROMOTED_SUCCESSFULLY'), 'success'));
            const firmwareFilter = getFirmwareFilter();
            await fetchRow(firmwareFilter);
        } else {
            setIsLoading(false);
            dispatch(startSnackbar(t('ERROR_DURING_PROMOTE_FIRMWARE'), 'error'));
        }
    };

    return (
        <MainCard title="Firmwares" content={false}>
            <CardContent>
                <Grid container justifyContent="space-between" sx={{ mb: '1vh' }}>
                    <Drawer
                        anchor="right"
                        open={fliterDialogModalOpen}
                        onClose={() => setFliterDialogModalOpen(false)}
                        PaperProps={{
                            sx: { width: { xs: '75%', md: '50%', lg: '25%' } }
                        }}
                    >
                        <Grid container direction="column" spacing={2} sx={{ p: '15px' }}>
                            <Grid item justifyContent="center" textAlign="center">
                                <Grid container direction="row" alignItems="center">
                                    <Typography textAlign="center" variant="h5">
                                        {t('ADVANCED_FILTERS')}
                                    </Typography>
                                    <Tooltip title={t('CLOSE')}>
                                        <IconButton
                                            sx={{ marginLeft: 'auto', p: 0 }}
                                            onClick={() => {
                                                setFliterDialogModalOpen(false);
                                            }}
                                        >
                                            <HighlightOffIcon color="error" fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('ORDER_BY')}</InputLabel>
                                    <Select label={t('ORDER_BY')} name="orderFieldName" onChange={handleChangeOrder} value={orderBy}>
                                        <MenuItem value="version">{t('VERSION')}</MenuItem>
                                        <MenuItem value="releaseDate">{t('RELEASE_DATE')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('ORDER_METHOD')}</InputLabel>
                                    <Select
                                        label={t('ORDER_METHOD')}
                                        name="isDescending"
                                        onChange={handleChangeOrderMethod}
                                        sx={{ color: 'white' }}
                                        value={order}
                                    >
                                        <MenuItem value="asc">{t('ASCENDING')}</MenuItem>
                                        <MenuItem value="desc">{t('DESCENDING')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    startIcon={<CloseIcon fontSize="large" />}
                                    variant="outlined"
                                    color="error"
                                    onClick={() => {
                                        setDefaultFilters();
                                        setFliterDialogModalOpen(false);
                                    }}
                                >
                                    {t('REMOVE_FILTERS')}
                                </Button>
                            </Grid>
                        </Grid>
                    </Drawer>

                    <Grid item lg={12} xs={12} container>
                        <Grid lg={4} md={4} item xs={6}>
                            <TextField
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                                onChange={handleSearch}
                                placeholder={mobileDevice ? t('SEARCH') : t('SEARCH_FIRMWARE')}
                                value={searchText}
                            />
                        </Grid>
                        <Grid lg={4} md={4} item xs={2} zeroMinWidth />
                        <Grid
                            lg={4}
                            md={4}
                            xs={4}
                            item
                            sx={{ display: 'flex', justifyContent: mobileDevice ? 'space-around' : 'flex-end' }}
                        >
                            <Grid
                                item
                                lg={2}
                                md={2}
                                xs={windowWidth < 400 ? 3 : 4}
                                sx={{
                                    display: 'flex',
                                    justifyContent: windowWidth < 400 ? 'flex-start' : 'flex-end',
                                    textAlign: 'right'
                                }}
                            >
                                <Stack direction="row" spacing={-1}>
                                    <Tooltip title={t('FILTERS')}>
                                        <IconButton onClick={() => setFliterDialogModalOpen(true)}>
                                            <FilterAltIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Grid>
                            <Grid item xs={1} sx={{ textAlign: 'right', paddingTop: 0.9 }}>
                                <Tooltip title={t('ADD_FIRMWARE')}>
                                    <IconButton
                                        className="addButton"
                                        sx={{ backgroundColor: theme.palette.primary.main }}
                                        size="small"
                                        onClick={() =>
                                            setFirmwareToAdd({
                                                id: '',
                                                major: 0,
                                                minor: 0,
                                                internal: false,
                                                releaseDate: new Date(Date.now()),
                                                releaseNote: '',
                                                zipFile: new ArrayBuffer(0)
                                            })
                                        }
                                    >
                                        <AddIcon sx={{ color: '#fff', fontSize: '17px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
            {/* table */}
            {rows ? (
                <>
                    {mobileDevice ? (
                        <FirmwareListMobile
                            firmwaresList={rows}
                            onEdit={(row) => setFirmwareToEdit(row)}
                            downloadFirmware={downloadFirmware}
                            promoteToProduction={(row) => {
                                setShowConfirmationDialog(true);
                                setFirmwareToPromote(row);
                            }}
                            isLoading={isLoading}
                        />
                    ) : (
                        <TableContainer sx={{ pl: '2px', pr: '2px' }}>
                            <Table
                                stickyHeader
                                sx={{
                                    minWidth: 750,
                                    '& .MuiTableCell-root': { padding: '2px 8px' }
                                }}
                                aria-labelledby="tableTitle"
                            >
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={handleRequestSort}
                                    onSelectAllClick={handleSelectAllClick}
                                    rowCount={rows.length}
                                    selected={selected}
                                />
                                {!isLoading ? (
                                    <TableBody>
                                        {rows.map((row, index) => {
                                            if (typeof row === 'number') return null;
                                            const isItemSelected = isSelected(row.id);
                                            const labelId = `enhanced-table-checkbox-${index}`;

                                            return (
                                                <TableRow
                                                    hover
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={row.id}
                                                    selected={isItemSelected}
                                                >
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{ color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900' }}
                                                        >
                                                            {`${row.major?.toString()}.${row.minor?.toString()}`}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                                                                float: 'left'
                                                            }}
                                                        >
                                                            {row.releaseDate
                                                                ? utilities.formatDate(row.releaseDate.toString(), locale)
                                                                : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" sx={{ cursor: 'pointer' }}>
                                                        <Typography
                                                            variant="subtitle1"
                                                            sx={{
                                                                color: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.900',
                                                                textAlign: 'center',
                                                                display: 'flex',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            {row.internal && (
                                                                <CheckIcon sx={{ fontSize: '1.3rem', marginY: '2px' }} color="secondary" />
                                                            )}
                                                            {!row.internal && (
                                                                <ClearIcon sx={{ fontSize: '1.3rem', marginY: '2px' }} color="error" />
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ pr: 3, '& .MuiTableCell-root': { padding: '4px 8px' } }}>
                                                        <Tooltip title={t('DOWNLOAD_FIRMWARE')}>
                                                            <IconButton
                                                                color="secondary"
                                                                size="large"
                                                                onClick={() => downloadFirmware(row)}
                                                            >
                                                                <DownloadIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('RELEASE_NOTES')}>
                                                            <IconButton
                                                                color="secondary"
                                                                size="large"
                                                                onClick={() =>
                                                                    navigate(`/view-firmwar-details`, { state: { firmware: row } })
                                                                }
                                                            >
                                                                <NoteIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('EDIT')}>
                                                            <IconButton
                                                                color="secondary"
                                                                size="large"
                                                                onClick={() => setFirmwareToEdit(row)}
                                                            >
                                                                <EditTwoToneIcon sx={{ fontSize: '1.3rem' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {row.internal && (
                                                            <Tooltip title={t('PROMOTE_TO_PRODUCTION')}>
                                                                <IconButton
                                                                    color="secondary"
                                                                    size="large"
                                                                    onClick={() => {
                                                                        setShowConfirmationDialog(true);
                                                                        setFirmwareToPromote(row);
                                                                    }}
                                                                >
                                                                    <StarIcon sx={{ fontSize: '1.3rem' }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                ) : (
                                    <SkeletonBody numHeaders={3} showAction />
                                )}
                            </Table>
                        </TableContainer>
                    )}
                </>
            ) : (
                <Skeleton variant="rectangular" width={210} height={118} />
            )}
            {/* table pagination */}
            <TablePagination
                sx={{
                    ...(mobileDevice && {
                        display: 'flex',
                        '& .MuiTablePagination-actions': {
                            marginLeft: 0
                        },
                        '& .MuiInputBase-root': {
                            marginRight: 1
                        },
                        '& .MuiIconButton-root': {
                            padding: '6px'
                        }
                    })
                }}
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalElements}
                rowsPerPage={rowsPerPage}
                page={page}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('OF')} ${count}`}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('ROWS_PER_PAGE')}
            />
            <FirmwareEdit firmware={firmwareToEdit} onSave={onSave} onClose={() => setFirmwareToEdit(undefined)} />
            <FirmwareAdd firmware={firmwareToAdd} onAdd={onAdd} onClose={() => setFirmwareToAdd(undefined)} />
            <ConfirmatioDialog
                isVisible={showConfirmationDialog}
                titleText="CONFIRM_UPDATE"
                bodyText="ARE_YOU_SURE_TO_UPDATE_TO_PRODUCTION_FIRMWARE"
                onClose={() => {
                    setShowConfirmationDialog(false);
                }}
                onConfirm={() => {
                    setShowConfirmationDialog(false);
                    setFirmwareToPromote(undefined);
                    promoteFirmware(firmwareToPromote!.id);
                }}
            />
        </MainCard>
    );
};

export default FirmwareList;
